import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useRouterState } from '@tanstack/react-router';
import { Bell, ExternalLink, LogOut, Search, ShieldCheck, User, UserRoundCog } from 'lucide-react';
import { filterNavSections } from '@blimo/shared';
import {
  Avatar,
  AvatarFallback,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  initials,
  ThemeToggle,
  BrandLogo,
} from '@blimo/ui';
import { adminNav } from '../nav.js';
import { useLogout } from '../lib/use-logout.js';
import { useApi, useStopImpersonation } from '@blimo/api-client/react';
import { adminQueries } from '@blimo/api-client';
import { env } from '../lib/api.js';
import { activeNavTarget } from '../lib/nav-utils.js';
import { GlobalSearch } from './global-search.jsx';

/**
 * @param {{ me: import('@blimo/api-client').Me, children: import('react').ReactNode }} props
 */
export function AppShell({ me, children }) {
  const sections = useMemo(() => filterNavSections(adminNav, me), [me]);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const activeTo = activeNavTarget(
    pathname,
    sections.flatMap((section) => section.items.map((item) => item.to)),
  );

  return (
    <SidebarProvider>
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded-md focus:bg-background focus:px-3 focus:py-2"
      >
        Zum Inhalt springen
      </a>
      <Sidebar label="Hauptnavigation">
        <SidebarHeader>
          <BrandLogo variant="mark" alt="" className="hidden h-5 group-data-[collapsed=true]/sidebar:block" />
          <span className="flex min-w-0 flex-col gap-0.5 group-data-[collapsed=true]/sidebar:sr-only">
            <BrandLogo className="h-9" />
            <span className="truncate text-xs font-medium text-muted-foreground">Verwaltung</span>
          </span>
        </SidebarHeader>
        <SidebarContent>
          {sections.map((section) => (
            <SidebarGroup key={section.id}>
              {section.label && <SidebarGroupLabel>{section.label}</SidebarGroupLabel>}
              <SidebarMenu>
                {section.items.map((item) => {
                  const Icon = item.icon;
                  const Badge = item.badge ? NAV_BADGES[item.badge] : null;
                  return (
                    <SidebarMenuItem key={item.id}>
                      <SidebarMenuButton asChild isActive={item.to === activeTo} tooltip={item.label}>
                        <Link to={item.to}>
                          <Icon aria-hidden="true" />
                          <span>{item.label}</span>
                          {Badge && <Badge />}
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroup>
          ))}
        </SidebarContent>
      </Sidebar>
      <SidebarInset>
        {me.impersonating && <ImpersonationBanner me={me} />}
        <Topbar me={me} />
        <main id="main" className="flex-1 px-4 py-5 md:px-6">
          {children}
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
}

/** Open owner messages (`meta.open` of GET /admin/messages) next to „Posteingang“. Only mounted when the item is visible. */
function MessagesOpenBadge() {
  const api = useApi();
  const { data: open } = useQuery(adminQueries.messagesOpenCount(api));
  if (!open) return null;
  return (
    <span
      data-testid="nav-badge-messages-open"
      className="numeric ml-auto rounded-full bg-primary px-1.5 text-[11px] font-semibold leading-5 text-primary-foreground group-data-[collapsed=true]/sidebar:hidden"
    >
      <span aria-hidden="true">{open > 99 ? '99+' : open}</span>
      <span className="sr-only">({open} offen)</span>
    </span>
  );
}

const NAV_BADGES = { 'messages-open': MessagesOpenBadge };

function ImpersonationBanner({ me }) {
  const stop = useStopImpersonation();
  return (
    <div role="status" className="flex items-center gap-2 bg-warning px-4 py-1.5 text-sm font-medium text-warning-foreground">
      <UserRoundCog className="size-4 shrink-0" aria-hidden="true" />
      <span className="flex-1">
        Sie handeln als <strong>{me.name}</strong> (Impersonation). Alle Aktionen werden im Änderungsprotokoll mit Ihrem Admin-Konto protokolliert.
      </span>
      {me.apps?.portal && (
        <Button asChild size="sm" variant="outline">
          <a href={env.portalUrl} target="_blank" rel="noreferrer">
            <ExternalLink aria-hidden="true" /> Portal öffnen
          </a>
        </Button>
      )}
      <Button size="sm" variant="outline" onClick={() => stop.mutate()} disabled={stop.isPending}>
        Benutzeransicht beenden
      </Button>
    </div>
  );
}

function Topbar({ me }) {
  const [searchOpen, setSearchOpen] = useState(false);
  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);

  useEffect(() => {
    function onKeyDown(event) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setSearchOpen((open) => !open);
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <header className="sticky top-0 z-30 flex h-14 items-center gap-2 border-b bg-background/95 px-3 backdrop-blur md:px-4">
      <SidebarTrigger />
      <button
        type="button"
        onClick={() => setSearchOpen(true)}
        className="flex h-(--control-height) w-full min-w-0 max-w-md items-center gap-2 rounded-md border border-input bg-card px-3 text-left text-sm text-muted-foreground hover:bg-accent"
        aria-label="Globale Suche öffnen"
        aria-keyshortcuts="Meta+K Control+K"
      >
        <Search className="size-4 shrink-0" aria-hidden="true" />
        <span className="min-w-0 flex-1 truncate">
          <span className="sm:hidden">Suchen …</span>
          <span className="hidden sm:inline">Liegenschaft, Einheit, Person, Dokument suchen …</span>
        </span>
        <kbd className="hidden rounded border bg-muted px-1.5 font-mono text-[11px] sm:inline">{isMac ? '⌘K' : 'Strg K'}</kbd>
      </button>
      <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />

      <div className="ml-auto flex shrink-0 items-center gap-0.5 sm:gap-1">
        <ThemeToggle />
        <NotificationBell />
        <UserMenu me={me} />
      </div>
    </header>
  );
}

function NotificationBell() {
  return (
    <DropdownMenu>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" aria-label="Benachrichtigungen">
              <Bell />
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent>Benachrichtigungen</TooltipContent>
      </Tooltip>
      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuLabel>Benachrichtigungen</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <p className="px-2 py-4 text-center text-sm text-muted-foreground">Keine Benachrichtigungen.</p>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function UserMenu({ me }) {
  const logout = useLogout();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="gap-2 px-1.5" aria-label={`Benutzermenü für ${me.name}`}>
          <Avatar className="size-7">
            <AvatarFallback>{initials(me.name)}</AvatarFallback>
          </Avatar>
          <span className="hidden max-w-40 truncate text-sm lg:inline">{me.name}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        <DropdownMenuLabel className="flex flex-col gap-0.5">
          <span className="truncate">{me.name}</span>
          <span className="truncate text-xs font-normal text-muted-foreground">{me.email}</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/profil" search={{ tab: 'profil' }}>
            <User aria-hidden="true" /> Profil
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/profil" search={{ tab: 'zwei-faktor' }}>
            <ShieldCheck aria-hidden="true" /> Zwei-Faktor
          </Link>
        </DropdownMenuItem>
        {me.apps?.portal && (
          <DropdownMenuItem asChild>
            <a href={env.portalUrl}>
              <ExternalLink aria-hidden="true" /> Zum Kundenportal
            </a>
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => logout()}>
          <LogOut aria-hidden="true" /> Abmelden
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
